<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { Gemeinde, GemeindeCollection } from '@/models/Gemeinde';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';
import KpKantonSelect from '@/components/KpKantonSelect.vue';
import KpTextField from '@/components/KpTextField.vue';
import KpNumberField from '@/components/KpNumberField.vue';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'kanton' },
    { direction: 'asc', property: 'bezeichnung' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new GemeindeCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: Gemeinde) {
    router.push({ name: 'bg_allg_gemeinde_edit', params: { id: r?.id } });
}

async function deleteRecord(r: Gemeinde) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_allg_gemeinde_edit', params: { id: 'new' } });
}

type Filter = {
    property: string;
    value: any;
    id: string;
    operator?: string;
};
const filters = ref({
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    bezeichnung: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bfs_nummer: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            operator: key === 'bezeichnung' ? 'icontains' : '=',
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:filters="filters"
            v-model:table-sort="tableSort"
            :title="$t('gemeinden')"
            scrollable
            scroll-height="flex"
            filterDisplay="row"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as Gemeinde)"
            @deleterecord="deleteRecord($event as Gemeinde)"
            @new="newRecord"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="bezeichnung" sortable :header="$t('bezeichnung')" :showFilterMenu="false">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField fluid v-model="filterModel.value" @change="filterCallback()"></KpTextField>
                </template>
            </Column>
            <Column field="bfs_nummer" sortable :header="$t('bfs_nummer')" :showFilterMenu="false">
                <template #filter="{ filterModel, filterCallback }">
                    <KpNumberField fluid v-model="filterModel.value" @change="filterCallback()"></KpNumberField>
                </template>
            </Column>
            <Column
                field="kanton_id"
                sortable
                :header="$t('kanton')"
                sort-field="kanton_kuerzel"
                :showFilterMenu="false"
            >
                <template #body="slotProps">{{ slotProps.data.kanton_kuerzel }}</template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpKantonSelect v-model="filterModel.value" fluid @change="filterCallback()"></KpKantonSelect>
                </template>
            </Column>
        </KpBaseCollectionGrid>
    </div>
</template>
